import {
 forwardRef,
 useRef,
 useState,
 type HTMLAttributes,
 type ReactNode,
} from "react";
import {
 Group as PanelGroup,
 Panel,
 Separator as PanelResizeHandle,
 useDefaultLayout,
 type PanelImperativeHandle,
} from "react-resizable-panels";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar.js";
import { Button } from "./button.js";
import { AgentCard, type Archetype } from "./agent-card.js";
import type { League } from "./league-badge.js";
import { cn } from "./lib/cn.js";

/**
 * The Mech Creator's three-column workspace shell per
 * `docs/client/architecture.md §4.1`:
 *
 * ┌──────────────┬─────────────────────────────┬───────────────┐
 * │ Agent rail │ Editor (loadout + prompt) │ Live preview │
 * │ │ │ │
 * │ - slot 1 │ │ │
 * │ - slot 2 │ │ │
 * │ + add slot │ │ │
 * │ │ │ │
 * │ ───────── │ │ │
 * │ user prof. │ │ │
 * └──────────────┴─────────────────────────────┴───────────────┘
 *
 * All three columns are resizable via `react-resizable-panels`. Sizes
 * persist via the `autoSaveId` prop (write to `localStorage`). The
 * left rail can collapse to a portrait-only mode — the parent doesn't
 * need to know how this works; collapse / expand is internal state.
 *
 * The center + right columns are content slots — the caller owns
 * what renders inside them. The left rail is opinionated: it accepts
 * a typed list of agents + an active index and renders the slot
 * column directly. This is because the rail's collapse behavior
 * needs to know about the agent list to swap to a portrait grid.
 */

export interface AgentSlot {
 id: string;
 /** Display name / handle. */
 name: string;
 /** Portrait URL — falls back to initials. */
 portraitUrl?: string;
 archetype?: Archetype;
 /** Current league for the rating badge. */
 league?: League;
 rating?: number;
 /** Provisional rating flag (under-played count). */
 provisional?: boolean;
}

export interface MechCreatorLayoutProps
 extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
 agents: readonly AgentSlot[];
 activeIndex: number;
 onSelectAgent: (index: number) => void;
 /** Click-handler for the "+ Add slot" button in the rail. */
 onAddAgent?: () => void;
 /** Content for the middle column (editor). */
 editor: ReactNode;
 /** Content for the right column (preview). */
 preview: ReactNode;
 /** Content for the user-profile area pinned at the bottom of the
 * left rail. Visible in both expanded and collapsed states. */
 userProfile?: ReactNode;
 /** Persisted layout key. Different Mech Creator instances should
 * pass different ids so they don't share sizes. */
 autoSaveId?: string;
 /** Default panel sizes (percent). Overridden by persisted layout. */
 defaultLeftSize?: number;
 defaultRightSize?: number;
}

export const MechCreatorLayout = forwardRef<
 HTMLDivElement,
 MechCreatorLayoutProps
>(
 (
 {
 agents,
 activeIndex,
 onSelectAgent,
 onAddAgent,
 editor,
 preview,
 userProfile,
 autoSaveId = "mech-creator-layout",
 defaultLeftSize = 22,
 defaultRightSize = 30,
 className,
 ...rest
 },
 ref,
 ) => {
 const leftPanel = useRef<PanelImperativeHandle | null>(null);
 const [collapsed, setCollapsed] = useState(false);

 // v4 of react-resizable-panels persists layout via this hook
 // (writes to localStorage by default). The id namespaces the saved
 // layout so different MechCreatorLayout instances don't collide.
 const { defaultLayout, onLayoutChanged } = useDefaultLayout({
 id: autoSaveId,
 panelIds: ["left", "center", "right"],
 });

 // v4 of react-resizable-panels removed the explicit onCollapse /
 // onExpand callbacks; we watch onResize and flip local state when
 // the asPercentage drops to the collapsedSize threshold. The 1%
 // hysteresis avoids flicker when the user drags right on the edge.
 const handleResize = (size: { asPercentage: number }) => {
 const isCollapsed = size.asPercentage <= 5.5;
 setCollapsed((prev) => (prev === isCollapsed ? prev : isCollapsed));
 };

 const toggleCollapsed = () => {
 const p = leftPanel.current;
 if (!p) return;
 if (collapsed) p.expand();
 else p.collapse();
 };

 return (
 <div
 ref={ref}
 className={cn(
 "h-full w-full overflow-hidden",
 "bg-[var(--background)] text-[var(--pp-text)]",
 className,
 )}
 {...rest}
 >
 <PanelGroup
 orientation="horizontal"
 defaultLayout={defaultLayout}
 onLayoutChanged={onLayoutChanged}
 >
 <Panel
 panelRef={leftPanel}
 id="left"
 defaultSize={defaultLeftSize}
 minSize={14}
 collapsible
 collapsedSize={5}
 onResize={handleResize}
 className="flex flex-col"
 >
 <AgentRail
 agents={agents}
 activeIndex={activeIndex}
 onSelectAgent={onSelectAgent}
 onAddAgent={onAddAgent}
 collapsed={collapsed}
 onToggleCollapsed={toggleCollapsed}
 userProfile={userProfile}
 />
 </Panel>

 <ResizeHandle />

 <Panel id="center" minSize={30} className="flex flex-col">
 <div className="flex-1 overflow-auto p-4">{editor}</div>
 </Panel>

 <ResizeHandle />

 <Panel
 id="right"
 defaultSize={defaultRightSize}
 minSize={20}
 collapsible
 collapsedSize={0}
 className="flex flex-col"
 >
 <div className="flex-1 overflow-auto p-4">{preview}</div>
 </Panel>
 </PanelGroup>
 </div>
 );
 },
);
MechCreatorLayout.displayName = "MechCreatorLayout";

// ---------------------------------------------------------------------------
// Agent rail — left column content
// ---------------------------------------------------------------------------

interface AgentRailProps {
 agents: readonly AgentSlot[];
 activeIndex: number;
 onSelectAgent: (index: number) => void;
 onAddAgent?: () => void;
 collapsed: boolean;
 onToggleCollapsed: () => void;
 userProfile?: ReactNode;
}

function AgentRail({
 agents,
 activeIndex,
 onSelectAgent,
 onAddAgent,
 collapsed,
 onToggleCollapsed,
 userProfile,
}: AgentRailProps) {
 return (
 <aside
 className={cn(
 "flex h-full flex-col border-r border-[var(--pp-border)]",
 "bg-[var(--pp-bg-strong)]",
 )}
 aria-label="Agent roster"
 >
 <header
 className={cn(
 "flex items-center justify-between gap-2 border-b border-[var(--pp-border)] p-3",
 collapsed && "justify-center px-2",
 )}
 >
 {!collapsed && (
 <h2 className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]">
 Agents
 </h2>
 )}
 <Button
 variant="ghost"
 size="sm"
 onClick={onToggleCollapsed}
 aria-label={collapsed ? "Expand agent rail" : "Collapse agent rail"}
 aria-expanded={!collapsed}
 >
 {collapsed ? "›" : "‹"}
 </Button>
 </header>

 <div className="flex-1 overflow-y-auto">
 {collapsed ? (
 <CollapsedRail
 agents={agents}
 activeIndex={activeIndex}
 onSelectAgent={onSelectAgent}
 />
 ) : (
 <ExpandedRail
 agents={agents}
 activeIndex={activeIndex}
 onSelectAgent={onSelectAgent}
 onAddAgent={onAddAgent}
 />
 )}
 </div>

 {userProfile && (
 <footer
 className={cn(
 "border-t border-[var(--pp-border)] p-2",
 collapsed ? "flex justify-center" : "",
 )}
 aria-label="User profile"
 >
 {userProfile}
 </footer>
 )}
 </aside>
 );
}

function ExpandedRail({
 agents,
 activeIndex,
 onSelectAgent,
 onAddAgent,
}: {
 agents: readonly AgentSlot[];
 activeIndex: number;
 onSelectAgent: (index: number) => void;
 onAddAgent?: () => void;
}) {
 return (
 <div className="flex flex-col gap-2 p-2" role="listbox">
 {agents.map((agent, idx) => {
 const active = idx === activeIndex;
 return (
 <button
 key={agent.id}
 type="button"
 role="option"
 aria-selected={active}
 onClick={() => onSelectAgent(idx)}
 className={cn(
 " text-left transition-[box-shadow]",
 "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]",
 active && "ring-2 ring-[var(--accent)]",
 )}
 >
 <AgentCard
 layout="row"
 agent={{
 handle: agent.name,
 league: agent.league ?? "small",
 rating: agent.rating ?? 1500,
 portraitUrl: agent.portraitUrl,
 archetype: agent.archetype,
 provisional: agent.provisional,
 }}
 />
 </button>
 );
 })}
 {onAddAgent && (
 <Button variant="outline" onClick={onAddAgent} className="mt-1">
 + Add Agent slot
 </Button>
 )}
 </div>
 );
}

function CollapsedRail({
 agents,
 activeIndex,
 onSelectAgent,
}: {
 agents: readonly AgentSlot[];
 activeIndex: number;
 onSelectAgent: (index: number) => void;
}) {
 return (
 <div className="flex flex-col items-center gap-2 py-2" role="listbox">
 {agents.map((agent, idx) => {
 const active = idx === activeIndex;
 return (
 <button
 key={agent.id}
 type="button"
 role="option"
 aria-selected={active}
 onClick={() => onSelectAgent(idx)}
 aria-label={`Select agent ${agent.name}`}
 title={agent.name}
 className={cn(
 "rounded-full transition-[box-shadow]",
 "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]",
 active && "ring-2 ring-[var(--accent)] ring-offset-2 ring-offset-[var(--background)]",
 )}
 >
 <Avatar size="md">
 {agent.portraitUrl ? (
 <AvatarImage src={agent.portraitUrl} alt={agent.name} />
 ) : (
 <AvatarFallback initials={initials(agent.name)} />
 )}
 </Avatar>
 </button>
 );
 })}
 </div>
 );
}

function initials(name: string): string {
 const trimmed = name.trim();
 if (!trimmed) return "?";
 const parts = trimmed.split(/[\s_.-]+/).filter(Boolean);
 if (parts.length === 1) {
 return parts[0]!.slice(0, 2).toUpperCase();
 }
 return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

// ---------------------------------------------------------------------------
// Shared resize handle
// ---------------------------------------------------------------------------

function ResizeHandle() {
 return (
 <PanelResizeHandle
 className={cn(
 "w-1 cursor-col-resize bg-[var(--pp-border)]",
 "transition-colors hover:bg-[var(--accent)]",
 "data-[resize-handle-state=hover]:bg-[var(--accent)]",
 "data-[resize-handle-state=drag]:bg-[var(--accent)]",
 )}
 />
 );
}

export type { PanelImperativeHandle };
